import { useFormikContext } from 'formik';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useSelectedFinderVehicles from '../../../shared/useSelectedFinderVehicles';
import useSelectedVariants from '../../../shared/useSelectedVariants';
import { type FinanceProductFormValues } from '../../index';
import { type Columns } from '../EditableTable';
import { type InsuranceFeeTableRecord } from './shared';
import useInsuranceFeeRecords from './useInsuranceFeeRecords';
import useInsuranceFeeVehicleReferenceParameterRecords from './useInsuranceFeeVehicleReferenceParameterRecords';

/**
 * Hook for preparing insurance fee table data
 */
const useInsuranceFeeReference = ({ isDisabled = true }: { isDisabled?: boolean }) => {
    const { t } = useTranslation(['financeProductDetails']);

    const { values, setFieldValue } = useFormikContext<FinanceProductFormValues>();

    const { variantSuiteIds, insuranceFee, displayName } = values;

    const selectedVariants = useSelectedVariants(variantSuiteIds);
    const selectedFinderVehicles = useSelectedFinderVehicles(variantSuiteIds);

    const variantRecords = useInsuranceFeeRecords(selectedVariants, insuranceFee);
    const finderVehiclesRecords = useInsuranceFeeRecords(selectedFinderVehicles, insuranceFee);
    const vehicleReferenceParameterRecords = useInsuranceFeeVehicleReferenceParameterRecords(
        values.vehicleReferenceParameters,
        t('financeProductDetails:suffix.mileage'),
        insuranceFee
    );

    const onFieldUpdate = useCallback(
        (record: InsuranceFeeTableRecord) => {
            const { variantSuiteId, value } = record;

            const current = [
                ...variantRecords,
                ...finderVehiclesRecords,
                vehicleReferenceParameterRecords.variant,
                vehicleReferenceParameterRecords.finderVehicle,
            ].filter(Boolean);

            // Trim out variant name is it not included inside payload
            const results = current.map(row => {
                if (row.variantSuiteId === variantSuiteId) {
                    return {
                        variantSuiteId,
                        value,
                    };
                }

                return { variantSuiteId: row.variantSuiteId, value: row.value };
            });

            setFieldValue('insuranceFee.table', results);
        },
        [variantRecords, finderVehiclesRecords, vehicleReferenceParameterRecords, setFieldValue]
    );

    const useColumns = useCallback(
        (title: string): Columns<InsuranceFeeTableRecord> => [
            {
                title,
                dataIndex: 'variantName',
                fixed: true,
                className: 'variant-column',
            },
            {
                title: t('financeProductDetails:tables.ucclLeasing.columnTitles.insuranceFeeValue'),
                dataIndex: 'value',
                onCell: record => ({
                    record,
                    dataIndex: 'value',
                    isEditable: !isDisabled,
                    onUpdate: onFieldUpdate,
                }),
            },
        ],
        [t, isDisabled, onFieldUpdate]
    );

    const variantColumns = useColumns(t('financeProductDetails:tables.ucclLeasing.columnTitles.variantName'));
    const finderVehicleColumns = useColumns(
        t('financeProductDetails:tables.ucclLeasing.columnTitles.finderVehicleName')
    );

    return {
        variantColumns,
        variantRecords: [...variantRecords, vehicleReferenceParameterRecords.variant].filter(Boolean),
        finderVehicleColumns,
        finderVehiclesRecords: [...finderVehiclesRecords, vehicleReferenceParameterRecords.finderVehicle].filter(
            Boolean
        ),
        displayName,
        insuranceFee,
    };
};

export default useInsuranceFeeReference;
